import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as maths from './maths';
import * as v3 from './vector3';
import * as THREE from 'three';
import { WrappedValue } from '@phi/wrapped-value';
import { withSpinner } from './state/spinner';
import { uid_deselect_all_objects } from './ui_display';
import {
    uil_drag_vertex_normal_manipulator,
    uil_get_edge_tangent_at_vertex,
    uil_get_vertices_of_edge,
    uil_is_object_fixed,
    uil_rotate_edge_list_around_axis,
    uil_get_face_geometry,
    uil_inflate_face,
    uil_is_object_visible,
    uil_make_faces_invisible,
    uil_make_faces_visible,
    uil_get_point_at_edge_param,
    uil_get_edges_of_vertex,
    uil_get_display_face_triangulation_res,
} from './ui_logic';
import {
    get_camera_look_vector,
    push_nodups,
    raycaster_intersect_objects,
    remove_tangencies_from_visible_invisible_edge_pairs,
    suppress_animate,
    suppress_undo_tracking,
    unsuppress_animate_if_ready,
} from './utility';
import { pointer_normalised } from './pointer';

export function find_closest_edge_to_point(edges, p) {
  var min_dist = 999999999;
  var min_eid = 0;
  edges.forEach(function(eid){
    var dist = min_point_edge_dist(eid, p);
    if (dist < min_dist) {
      min_eid = eid;
      min_dist = dist;
    }
  });
  return min_eid;
}

export function min_point_edge_dist(eid, p) {
  var e = uid_edges.getItem(eid);
  var mindist = 999999999;
  e.object3d.geometry.vertices.forEach(function(v) {
    var d = v3.point_point_distance(v, p);
    if (d < mindist)
      mindist = d
  });
  return mindist;
}

export function project_vector_on_plane(v, b, n) {
  var proj = new THREE.Vector3();
  var l = v.dot(n);
  proj.copy(v);
  proj.addScaledVector(n, -1 * l);

  return proj;
}

export function find_mirror_point(p, b, n) {
  var v = new THREE.Vector3();
  v.subVectors(p, b);
  var l = v.dot(n);
  v.copy(p);
  v.addScaledVector(n, -2 * l);
  return v;
}

export function edge_position_wrt_plane(b, n, eid, eps = 1e-3) {
  // Returns 1 if edge is in the half-space pointed to by the plane normal,
  // -1 if on the other half-plane, or 0 if in both. Based on the edge vertices.
  var vs = uil_get_vertices_of_edge(eid);
  var p0 = uid_vertices.getItem(vs[0]).object3d.position;
  var p1 = uid_vertices.getItem(vs[1]).object3d.position;
  var v = new THREE.Vector3();
  v.subVectors(p0, b);
  var l0 = v.dot(n);
  v.subVectors(p1, b);
  var l1 = v.dot(n);
  if (l0 >= eps && l1 >= eps)
    return 1;
  else if (l0 <= (-1) * eps && l1 <= (-1) * eps)
    return -1;
  else {
    // Check average distance from plane
    if (l0 + l1 > eps)
      return 1
    else if (l0 + l1 < (-1) * eps)
      return -1
    else
      return 0;
  }
}


export function mirror_point(p, base, normal) {
  var p1 = v3.project_point_on_plane(p, base, normal);
  var v = new THREE.Vector3();
  v.subVectors(p1,p).normalize();
  var d = v3.point_point_distance(p,p1);
  var mirror_point = new THREE.Vector3();
  v.multiplyScalar(d);
  mirror_point.addVectors(p1, v);
  return mirror_point;
}


export function triangle_normal(p1, p2, p3) {
  var v = new THREE.Vector3();
  var w = new THREE.Vector3();
  var n = new THREE.Vector3();
  v.subVectors(p2, p1);
  w.subVectors(p3, p1);
  n.crossVectors(v, w).normalize();
  if (n.length() == 0)
    return new THREE.Vector3(1.0, 0.0, 0.0);
  return n;
}

export function face_is_flat(fid, loose_threshold = false) {
  var b = new THREE.Vector3();
  var d = new THREE.Vector3();
  var n = new THREE.Vector3();
  var wv_r = new WrappedValue(0);
  var wv_f = new WrappedValue(0);
  uil_get_face_geometry(fid, b, n, wv_r, d, wv_f);

  // console_log("Flatness:", wv_f.v);

  var flatness_threshold = 0.00001;
  if (loose_threshold)
    flatness_threshold = 0.01;

  if (wv_f.v < flatness_threshold)
    return 1;
  else return 0;
}

export function calculate_sample_planar_points_for_add_edge(m, p0, p1, f_id, d, sample_points, sample_normals) {
  sample_points[0] = p0;
  sample_points[m-1] = p1;
  sample_normals[0] = new THREE.Vector3(0,0,0);
  sample_normals[m-1] = new THREE.Vector3(0,0,0);

  for (var i = 1; i < m-1; i++) {
    var t = i / (m-1);
    var b = new THREE.Vector3();
    b.subVectors(p1, p0);
    b.multiplyScalar(t);
    b.add(p0);

    // Set up a camera to do the raytracing
    var farb = new THREE.Vector3();
    farb.copy(d);
    farb.multiplyScalar(-100);
    farb.add(b);

    uid_raycaster.set(farb, d);
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    var intersections = 0;
    var q = new THREE.Vector3();
    var n = new THREE.Vector3();
    intersects.forEach(function(o) {
      var obj = uid_object3dIDToUid_object.getItem(o.object.id);
      if (obj && obj.id == f_id) {
        intersections++;
        //XXX console_log("i: ",i);
        q.copy(o.point);
        n.copy(o.face.normal);
      }
    });
    if (intersections != 1) {
      // Abort
      console_log_xml("Exiting calculate_sample_planar_points_for_add_edge");
      return 0;
    }
    else {
      sample_points[i] = q;
      sample_normals[i] = n;
    }
  }

  // Fix the first and last normals by copying the 2nd and one-before-last respectively
  sample_normals[0].copy(sample_normals[1]);
  sample_normals[m-1].copy(sample_normals[m-2]);

  return 1;
}

export function rotate_to_closest_global_coordinate_axis_step(manipulator, step) {

  // We perform 2 rotations
  var v1 = new THREE.Vector3();
  v1.copy(manipulator.local_x_direction);
  var v2 = new THREE.Vector3();
  var mina = 1000;
  var vec = new THREE.Vector3();
  for (var phi = 0; phi <= 2 * Math.PI*1.01; phi += step) {
    for (var theta = 0; theta <= 2 * Math.PI*1.01; theta += step) {
      vec.set(Math.cos(theta) * Math.cos(phi), Math.sin(theta) * Math.cos(phi), Math.sin(phi));
      var a = v1.angleTo(vec);
      if (a < mina) {
        mina = a;
        v2.copy(vec);
      }
    }
  }
  var vx = new THREE.Vector3();
  vx.crossVectors(v1, v2)
  var angle = v3.cc_angle(v1, v2, vx);
  if (angle != 0) {
    if (manipulator.rigid_move_edge_object_ids.length == 0 && !manipulator.rigid_move_face_object_id &&
      manipulator.rigid_move_vertex_object_ids.length == 1 )
      uil_drag_vertex_normal_manipulator(manipulator.rigid_move_vertex_object_ids[0], vx, angle);
    else
      uil_rotate_edge_list_around_axis(manipulator.rigid_move_edge_object_ids, manipulator.center, vx, angle);

    uid_state.in_first_drag_step = false;

    var adjusted_angle = angle;
    var adjusted_axis = new THREE.Vector3();
    adjusted_axis.copy(vx);
    if (angle > Math.PI)
      adjusted_angle = -1 * angle;
    uid_rigid_move_manipulator.rotate_around_axis(uid_rigid_move_manipulator.center, adjusted_axis, adjusted_angle);
  }

  v1.copy(manipulator.local_y_direction);
  var mina = 1000;
  var vec = new THREE.Vector3();
  for (var phi = 0; phi <= 2 * Math.PI * 1.01; phi += step) {
    for (var theta = 0; theta <= 2 * Math.PI * 1.01; theta += step) {
      vec.set(Math.cos(theta) * Math.cos(phi), Math.sin(theta) * Math.cos(phi), Math.sin(phi));
      var a = v1.angleTo(vec);
      if (a < mina) {
        mina = a;
        v2.copy(vec);
      }
    }
  }


  vx.crossVectors(v1, v2)
  var angle = v3.cc_angle(v1, v2, vx);
  if (angle != 0) {
    if (manipulator.rigid_move_edge_object_ids.length == 0 && !manipulator.rigid_move_face_object_id &&
      manipulator.rigid_move_vertex_object_ids.length == 1 )
      uil_drag_vertex_normal_manipulator(manipulator.rigid_move_vertex_object_ids[0], vx, angle);
    else
      uil_rotate_edge_list_around_axis(manipulator.rigid_move_edge_object_ids, manipulator.center, vx, angle);

    var adjusted_angle = angle;
    var adjusted_axis = new THREE.Vector3();
    adjusted_axis.copy(vx);
    if (angle > Math.PI)
      adjusted_angle = -1 * angle;
    uid_rigid_move_manipulator.rotate_around_axis(uid_rigid_move_manipulator.center, adjusted_axis, adjusted_angle);
  }
}

export function get_circle_from_three_points(A, B, C, Q, norm, wv_radius) {
/* It will calculate a centre (O) and radius from three input vectors (A,B,C), as well as a normal ('norm') to their plane. It will return 0 if collinear. The fn v3.angle returns angle between 2 unit direction vectors. The variables rA,rB,rC used below are simply random constants (with full precision) between 0 and 1. There is also a test for collinearity that uses a variable 'diam'. You should use your own epsilon for a normal collinearity test or just set 'diam' to something reasonable if you want to use the code as is.
*/

  // Check for collinear points
  var vtmp = new THREE.Vector3();
  var vtmp1 = new THREE.Vector3();
  var O = new THREE.Vector3();
  var eps = 1e-05;
  var rA = 0.316227766;
  var rB = 0.948683298;
  var rC = 0.572665981;

  vtmp.subVectors(B,A);
  vtmp1.subVectors(C,A);
  vtmp.crossVectors(vtmp,vtmp1);
  var area = vtmp.length() / 2.0;
  if ( area < eps ) {
      // Points are collinear
      return false;
  }
  // Find centre of circle
  O.copy(A).multiplyScalar(rA);
  vtmp.copy(B).multiplyScalar(rB);
  O.add(O,vtmp);
  vtmp.copy(C).multiplyScalar(rC);
  O.add(O,vtmp);
  O.multiplyScalar(1.0/(rA+rB+rC));
  var x = new THREE.Vector3();
  var y = new THREE.Vector3();
  x.subVectors(A,O).normalize();
  y.subVectors(B,O).normalize();
  vtmp.crossVectors(x,y).normalize();
  y.crossVectors(vtmp,x).normalize();
  vtmp.subVectors(A,O);
  var Ax = vtmp.dot(x);
  var Ay = vtmp.dot(y);
  vtmp.subVectors(B,O);
  var Bx = vtmp.dot(x);
  var By = vtmp.dot(y);
  vtmp.subVectors(C,O);
  var Cx = vtmp.dot(x);
  var Cy = vtmp.dot(y);
  // Use formulas for circle
  var x1 = Ax;
  var y1 = Ay;
  var x2 = Bx;
  var y2 = By;
  var x3 = Cx;
  var y3 = Cy;
  var ma = (y2-y1) / (x2-x1);
  var mb = (y3-y2) / (x3-x2);
  var Wx = (ma*mb*(y1-y3) + mb*(x1+x2) - ma*(x2+x3)) / (2.0*(mb-ma));
  var Wy = -(1.0/ma)*(Wx-(x1+x2)/2.0) + (y1+y2)/2.0;
  vtmp.copy(x).multiplyScalar(Wx);
  vtmp.add(O);
  vtmp1.copy(y).multiplyScalar(Wy);
  Q.addVectors(vtmp,vtmp1);
  wv_radius.v = v3.point_point_distance(Q,A);
  // Calculate a normal vector for plane of the circle
  var va = new THREE.Vector3();
  var vb = new THREE.Vector3();
  var vc = new THREE.Vector3();
  va.subVectors(A,Q).normalize();
  vb.subVectors(B,Q).normalize();
  vc.subVectors(C,Q).normalize();
  var thetaAB = v3.angle(va,vb);
  var thetaBC = v3.angle(vb,vc);
  //thetaCA = v3.angle(&vc,&va);
  if ( thetaAB < thetaBC )
      norm.crossVectors(va,vb).normalize();
  else
      norm.crossVectors(vb,vc).normalize();
  return true;
}


/* The following is test code for "slave edges" that are created by projecting on faces lines drawn onto planes

// Some temporary global vars for testing out the following function
var line_dir = new THREE.Vector3(1,0,0);
var line_origins = [];
for (i = 0; i < 180; i+=10) {
  var p = new THREE.Vector3();
  p.x = 0;
  p.y = Math.sin(i*Math.PI/180);
  p.z = Math.cos(i*Math.PI/180);
  line_origins.push(p);
}

function project_stored_lines_onto_surfaces() {
  suppress_animate();
  clear_small_balls();
  uid_faces.keys().forEach(function(fid){
    if (uil_is_object_visible(fid)) {
      line_origins.forEach(function(p){

        // Set up raycaster and find intersections
        var farb = new THREE.Vector3();
        farb.copy(line_dir);
        farb.multiplyScalar(-100);
        farb.add(p);
        uid_raycaster.set(farb, line_dir);
        var ray_intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
        var o = find_intersection_face_object(ray_intersects);
        if (o)
          add_small_ball(o.point.x, o.point.y, o.point.z, 200, 50, 50);

        // More accurate way but takes about 100msec per hit
        // var wv_u = new WrappedValue();
        // var wv_v = new WrappedValue();
        // var intersection_point = logged_jcore_get_face_ray_intersection(fid, p, line_dir, wv_u, wv_v);
        // if (intersection_point)
        //   add_small_ball(intersection_point.x, intersection_point.y, intersection_point.z, 200, 50, 50);
      });
    }
  });
  unsuppress_animate_if_ready();
}
*/

export function avg_distance_of_face_from_points_along_axis(fid, points, axis, check_for_hits, sample_threshold) {
  var dist = 0;
  var hits = 0;
  var wv_bogus = new WrappedValue();
  points.forEach(function(p){
    var new_p = uil_get_face_ray_intersection(fid, p, axis, wv_bogus, wv_bogus, false);
    if (new_p) {
      hits++;
      var x = v3.point_point_distance(p, new_p);
      dist += Math.pow(x, 2);
    }
  });
  dist /= hits;

  // Check that we have at least "sample_threshold" hits on this surface, otherwise we don't have enough to go on and we should abort.
  if (check_for_hits && hits < sample_threshold)
    return -1;
  return Math.sqrt(dist);
}


export function adjust_face_inflating_to_target(fid, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, sample_threshold) {

  if (uil_get_edges_of_face(fid).length < 4)
    // Faces with less than 4 edges can't be inflated/deflated, don't bother.
    return;

  var dist_current = avg_distance_of_face_from_points_along_axis(fid, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, true, sample_threshold);
  if (dist_current < 0) {
    console_log("Aborting adjust_face_inflating_to_target due to few samples");
    // We don't have enough samples to proceed, so abort.
    return;
  }
  console_log("Proceeding with adjust_face_inflating_to_target.");

  console_log("Face id: " + fid);
  console_log("Current distance = " + dist_current);
  var i;
  var res;
  var hit_limit;
  var finished_iteration;
  var dist_new;

  // Start with 5 inflates per iteration.
  var inflate_times = 3;
  var direction = "inflate";

  var done = false;
  while (!done) {
    finished_iteration = false;
    hit_limit = false;
    i = inflate_times;

    if (direction == "inflate") {
      var res = uil_inflate_face(fid, inflate_times);
      console_log("Inflating X ", inflate_times);
    }
    else {
      var res = uil_deflate_face(fid, inflate_times);
      console_log("Deflating X ", inflate_times);
    }
    if (!res) {
      hit_limit = true;
      console_log("Reached limit");
    }


    /*
    while (!finished_iteration) {
      if (direction == "inflate") {
        var res = uil_inflate_face(fid);
        console_log("Inflating");
      }
      else {
        var res = uil_deflate_face(fid);
        console_log("Deflating");
      }
      if (!res) {
        hit_limit = true;
        console_log("Reached limit");
        finished_iteration = true;
      }
      i--;
      if (i == 0)
        finished_iteration = true;
    }
    */

    dist_current = dist_new;
    dist_new = avg_distance_of_face_from_points_along_axis(fid, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, false, sample_threshold);
    console_log("New distance = ", dist_new);
    if (hit_limit || dist_new > dist_current) {
      if (direction == "inflate")
        direction = "deflate";
      else
        direction = "inflate";
      inflate_times--;
      if (inflate_times == 0) {
        done = true;
        // One last check
        console_log("Last check");
        dist_current = dist_new;
        if (direction == "inflate") {
          var res = uil_inflate_face(fid, 1);
          console_log("Inflating");
        }
        else {
          var res = uil_deflate_face(fid, 1);
          console_log("Deflating");
        }
        dist_new = avg_distance_of_face_from_points_along_axis(fid, uid_add_edge_face_sample_points_before, uid_add_edge_face_normal_before, false, sample_threshold);
        if (dist_new > dist_current) {
          // Roll back last step.
          console_log("Rolling back");
          if (direction == "inflate") {
            var res = uil_deflate_face(fid, 1);
            console_log("Inflating");
          }
          else {
            var res = uil_inflate_face(fid, 1);
            console_log("Deflating");
          }
        }
      }
    }
  }
}


export function edge_is_straight(eid) {
  // Checks whether the edge is GEOMETRICALLY (not in terms of linearity flag) a stright line
  var e = uid_edges.getItem(eid);
  var p1 = e.object3d.geometry.vertices[0];
  var p2 = e.object3d.geometry.vertices[e.object3d.geometry.vertices.length-1];
  var d = new THREE.Vector3();
  d.copy(p2).sub(p1).normalize();
  var is_straight = true;
  var eps = 0.0005;
  e.object3d.geometry.vertices.forEach(function(v){
    if (v3.line_point_distance(p1, d, v) > eps)
      is_straight = false;
  });

  return is_straight;
}

export function sphere_ray_intersect_plus(O, r, b, d) {
  var C;
  var beta, gamma, D, lambda;
  var x;
  var b1;

  C = new THREE.Vector3(b.x, b.y, b.z);
  C.sub(O);

  beta = 2 * C.dot(d);
  gamma = C.dot(C) - r * r;
  D = beta * beta - 4 * gamma;

  if (D >= 0) {
    var lambda_m, lambda_p;
    lambda_m = (-beta - Math.sqrt(D)) / 2;
    lambda_p = (-beta + Math.sqrt(D)) / 2;
    if (lambda_p > 0 && lambda_m < 0) {
      lambda = lambda_p;
    } else if (lambda_p > 0 && lambda_m > 0) {
      lambda = lambda_m;
    } else {
      // ERROR
    }
    x = new THREE.Vector3(b.x, b.y, b.z);
    var d1 = new THREE.Vector3(d.x, d.y, d.z);
    x.add(d1.multiplyScalar(lambda));
    return x;
  } else {
    var v1 = new THREE.Vector3();
    v1.subVectors(O, b);
    lambda = v1.dot(d);
    if (lambda > 0) {
      var q = new THREE.Vector3(b.x, b.y, b.z);
      q.addScaledVector(d, lambda);
      var v2 = new THREE.Vector3();
      v2.subVectors(q, O);
      x = new THREE.Vector3(O.x, O.y, O.z);
      x.addScaledVector(v2, r);
      return x;
    } else {
      // Error: lambda < 0.
      console.log("Sphere_ray_intersect error");
      x = new THREE.Vector3(0,0,0);
      return x;
    }
  }
}

export function plane_ray_intersect(O, n, b, d, r=1) {
  // Finds the 3D coordinates of intersection between ray (b,d) and plane (O,n)

  var x = new THREE.Vector3();

  if (Math.abs(v3.angle(d, n) - Math.PI / 2) > 0.01) {
    var Ominusb, lambda;
    Ominusb = new THREE.Vector3(O.x - b.x, O.y - b.y, O.z - b.z);
    lambda = Ominusb.dot(n) / d.dot(n);
    x = new THREE.Vector3(b.x, b.y, b.z);
    x.addScaledVector(d, lambda);
  }
  else {
    var v, b1, n1, n2, d1;
    var k1, k2;
    v = new THREE.Vector3(O.x, O.y, O.z);
    b1 = new THREE.Vector3(b.x, b.y, b.z);
    n1 = new THREE.Vector3(n.x, n.y, n.z);
    k1 = v.sub(b).dot(n);
    b1.add(n1.multiplyScalar(k1));

    k2 = n.dot(d);
    n2 = new THREE.Vector3(n.x, n.y, n.z);
    d1 = new THREE.Vector3(d.x, d.y, d.z);
    d1.sub(n2.multiplyScalar(k2)).normalize();

    x = sphere_ray_intersect_plus(O, r, b1, d1);
  }
  return x;
}

export function find_edge_center_of_mass(eid) {
  var e = uid_edges.getItem(eid);
  var c = new THREE.Vector3();
  c.copy(e.object3d.geometry.vertices[0]);
  var first = true;
  e.object3d.geometry.vertices.forEach(function(v){
    if (!first)
      c.add(v);
    first = false;
  });
  c.multiplyScalar(1/e.object3d.geometry.vertices.length);
  return c;
}

export function optimal_face_triangles_for_drag(dragged_edges, dragged_vertices, drag_type) {

  let current_drag_triangles = uil_get_display_face_triangulation_res();
  let n = 200;
  console_log("Setting face triangles for drag to: ", n);

  if (drag_type == "finished_drag") {
    n = 200;
  }
  else if (drag_type == "move_vertex" || drag_type == "move_vertex_on_edge") {
    let es = [];
    dragged_vertices.forEach(function(vid) {
      uil_get_edges_of_vertex(vid).forEach(function(eid){
        push_nodups(eid, es);
      });
    });
    let ne = es.length;
    if (ne < 3)
      n = 1600;
    else
      n = Math.max(400, 3000/ne);
  }
  else if (drag_type == "deform_edge") {
    n = 1600;
  }
  else if (drag_type == "translate_edge_list" || drag_type == "rotate_edge_list") {
    n = Math.max(40, 600/dragged_edges.length);
  }
  else if (drag_type == "scale_edge_list") {
    n = 400;
  }

  n = Math.min(n, current_drag_triangles);

  //console.log("Drag triangles: " + n);
  return n;
}

// ---------------------------------------------------
//--------------- Dragging with manipulators ---------
// ---------------------------------------------------

export function get_dragged_manipulator_disk_rotation_angle(
    rotation_center,
    rotation_plane_normal,
    disk_radius,
    previous_ray_origin,
    previous_ray_direction,
    current_ray_origin,
    current_ray_direction,
) {
  var previous_intersection, current_intersection, v_previous, v_current, angle;

  previous_intersection = plane_ray_intersect( rotation_center, rotation_plane_normal, previous_ray_origin, previous_ray_direction, disk_radius );
  current_intersection = plane_ray_intersect( rotation_center, rotation_plane_normal, current_ray_origin, current_ray_direction, disk_radius );

  v_previous = new THREE.Vector3();
  v_current = new THREE.Vector3();
  v_previous.subVectors(previous_intersection, rotation_center).normalize();
  v_current.subVectors(current_intersection, rotation_center).normalize();
  angle = v3.cc_angle(v_previous, v_current, rotation_plane_normal);

  return angle;
}

export function get_dragged_manipulator_sphere_rotation_axis_angle( O, r, b1, d1, b2, d2 ) {
  // Returns the pair of rotation {axis, angle}
  var x1, x2, x1a, x2a, v1, v2;
  var l;
  var n = new THREE.Vector3();
  var a;

  x1 = sphere_ray_intersect_plus(O, r, b1, d1);
  x2 = sphere_ray_intersect_plus(O, r, b2, d2);

  x1a = new THREE.Vector3(x1.x, x1.y, x1.z);
  x2a = new THREE.Vector3(x2.x, x2.y, x2.z);
  x1a.sub(O).normalize();
  x2a.sub(O).normalize();

  var vs = new THREE.Vector3(x1a.x, x1a.y, x1a.z);
  vs.sub(x2a);
  var ls = vs.length();
  var va = new THREE.Vector3(x1a.x, x1a.y, x1a.z);
  va.add(x2a);
  var la = va.length();

  if (ls < 1e-12 || la < 1e-12) {
    var p = new THREE.Vector3(Math.random(), Math.random(), Math.random());
    p.normalize();
    n.crossVectors(p, x1a).normalize();
  }
  else {
    n.crossVectors(x1a, x2a).normalize();
  }

  a = v3.angle(x1a, x2a);
  var axis_angle = new Array();
  axis_angle[0] = n;
  axis_angle[1] = a;

  return axis_angle;
}

export function find_vertex_normal(vid, ray_base, ray_direction) {
  if (vid == 0) {
    console_log_xml("Error, seeking normal of vertex with invalid ID");
    return new THREE.Vector3(1,0,0);
  }
  if (uil_is_vertex_smooth(vid))
    return uil_get_vertex_normal(vid);
  else {
    var normal = new THREE.Vector3();
    uil_get_edges_of_vertex(vid).forEach(function(eid) {
      var n = find_edge_normal(eid, ray_base, ray_direction);
      normal.add(n);
    });
    normal.normalize();
    return normal;
  }
}

export function find_edge_normal(eid, ray_base, ray_direction) {
  var normal = null;
  var face_ids = uil_get_faces_of_edge(eid);
  var normal = new THREE.Vector3(0,0,0);
  var n1 = uil_get_face_normal_at_edge_point(face_ids[0], eid, ray_base, ray_direction);
  var n2 = uil_get_face_normal_at_edge_point(face_ids[1], eid, ray_base, ray_direction);
  if (!n1 || !n2)
    return null;
  normal.add(n1);
  normal.add(n2);
  normal.normalize();
  return normal;
}

export function find_face_normal(fid, intersected_obj) {
  var normal = new THREE.Vector3();
  normal.copy(intersected_obj.face.normal);
  return normal;
}

export function round_scalar_to_grid(hit_point, grid_origin, grid_step) {

  var grid_step_unitless = get_unitless_value_from_display_unit(grid_step);

  return Math.round((hit_point - grid_origin) / grid_step_unitless) * grid_step_unitless + grid_origin;
  //return Math.round(hit_point / grid_step_unitless) * grid_step_unitless;
}

export function get_grid_point( mousenormalized, grid_origin, grid_normal, grid_min, grid_max, grid_step ) {
  // Use raycaster to find the ray the mouse is at
  uid_raycaster.setFromCamera(mousenormalized, uid_camera);
  var O = new THREE.Vector3( uid_raycaster.ray.origin.x, uid_raycaster.ray.origin.y, uid_raycaster.ray.origin.z );
  var d = new THREE.Vector3( uid_raycaster.ray.direction.x, uid_raycaster.ray.direction.y, uid_raycaster.ray.direction.z );

  // Find grid plane normal vector
  var grid_normal_vector;
  if (grid_normal == "x")
    grid_normal_vector = new THREE.Vector3(1, 0, 0);
  else if (grid_normal == "y")
    grid_normal_vector = new THREE.Vector3(0, 1, 0);
  else
    grid_normal_vector = new THREE.Vector3(0, 0, 1);

  // Find point where the ray hits the grid plane
  var grid_plane_hit = plane_ray_intersect(grid_origin, grid_normal_vector, O, d, 1);

  var grid_x, grid_y, grid_z;
  if (grid_normal == "x") {
    grid_x = 0;
    grid_y = round_scalar_to_grid (grid_plane_hit.y, grid_origin.y, grid_step);
    grid_z = round_scalar_to_grid (grid_plane_hit.z, grid_origin.z, grid_step);
  }
  else if (grid_normal == "y") {
    grid_x = round_scalar_to_grid (grid_plane_hit.x, grid_origin.x, grid_step);
    grid_y = 0;
    grid_z = round_scalar_to_grid (grid_plane_hit.z, grid_origin.z, grid_step);
  }
  else if (grid_normal == "z") {
    grid_x = round_scalar_to_grid (grid_plane_hit.x, grid_origin.x, grid_step);
    grid_y = round_scalar_to_grid (grid_plane_hit.y, grid_origin.y, grid_step);
    grid_z = 0;
  }

  var gridpoint = new THREE.Vector3(grid_x, grid_y, grid_z);
  return gridpoint;
}

export function plane_point_distance(b, n, p) {
  var o = v3.project_point_on_plane(p, b, n);
  return v3.point_point_distance(o, p);
}

export function project_vertices(vertex_list) {
  vertex_list.forEach(function(vid){
    var v = new THREE.Vector3();
    v.copy(uid_vertices.getItem(vid).object3d.position);
    var pxy = new THREE.Vector3(v.x, v.y, 0);
    var pyz = new THREE.Vector3(0, v.y, v.z);
    var pxz = new THREE.Vector3(v.x, 0, v.z);

    clear_small_balls();
    //add_small_ball(pxy.x, pxy.y, pxy.z, 150, 150, 150, 3);
    //add_small_ball(pyz.x, pyz.y, pyz.z, 150, 150, 150, 3);
    //add_small_ball(pxz.x, pxz.y, pxz.z, 150, 150, 150, 3);
    uid_add_points_polyline_to_scene([v, pxy], 50, 50, 50);
    uid_add_points_polyline_to_scene([v, pyz], 50, 50, 50);
    uid_add_points_polyline_to_scene([v, pxz], 50, 50, 50);
  });
}

export function clone_edge() {
  if (uid_selected_edges.length > 0 && uid_selected_vertices.length == uid_selected_edges.length) {
    // Check that selected objects are fine
    var selected_objects_ok = true;
    for (var i = 0; i < uid_selected_edges.length; i++) {
      var eid = uid_selected_edges.getKeyAtIndex(i);
      var v1id = uid_selected_vertices.getKeyAtIndex(i);
      var vs = uil_get_vertices_of_edge(eid);
      var v2id;
      if (vs[0] == v1id)
        v2id = vs[1];
      else if (vs[1] == v1id)
        v2id = vs[0];
      else
        selected_objects_ok = false;
    }
    if (selected_objects_ok) {
      var e1 = uid_selected_edges.getKeyAtIndex(0);
      var v11 = uid_selected_vertices.getKeyAtIndex(0);
      var vs = uil_get_vertices_of_edge(e1);
      var v12;
      if (vs[0] == v11)
        v12 = vs[1];
      else
        v12 = vs[0];

      var edges_to_deform = uid_selected_edges.keys().slice();
      var vertices_to_deform = uid_selected_vertices.keys().slice();

      // Check no target edges are fixed
      var all_ok = true;
      for (var i = 1; i < edges_to_deform.length; i++) {
        if (uil_is_object_fixed(edges_to_deform[i]))
          all_ok = false;
      }

      if (!all_ok) {
        display_message_to_user(LOG.WARN, "You cannot modify the shape of fixed edges.");
        return;
      }

      suppress_undo_tracking();
      for (var i = 1; i < edges_to_deform.length; i++) {
        var e2 = edges_to_deform[i];
        var v21 = vertices_to_deform[i];
        var vs = uil_get_vertices_of_edge(e2);
        var v22;
        if (vs[0] == v21)
          v22 = vs[1];
        else
          v22 = vs[0];
        make_edge_a_scaled_copy_of_other_edge(e1, v11, v12, e2, v21, v22);
      }
      unsuppress_undo_tracking_if_ready(false);
    }
    else {
      display_message_to_user(LOG.WARN, "You must select edges and pairs of vertices in the correct sequence for cloning.");
    }
  }
  else {
    display_message_to_user(LOG.WARN, "You must select edges and pairs of vertices in the correct sequence for cloning.");
  }
}

export function get_inward_looking_edge_tangent_at_vertex(edge_id, vertex_id) {
  if (uil_get_vertices_of_edge(edge_id).indexOf(vertex_id) == -1) {
    display_message_to_user(LOG.WARN, "Vertex must lie on edge");
    return null;
  }
  var t = uil_get_edge_tangent_at_vertex(edge_id, vertex_id);

  var g = uid_edges.getItem(edge_id).object3d.geometry;
  var s = new THREE.Vector3();
  var sprev = new THREE.Vector3();

  if (uid_vertices.getItem(vertex_id).object3d.position.distanceTo(g.vertices[0]) <
      uid_vertices.getItem(vertex_id).object3d.position.distanceTo(g.vertices[g.vertices.length-1])) {
    s.copy(g.vertices[0]);
    sprev.copy(g.vertices[1]);
  }
  else {
    s.copy(g.vertices[g.vertices.length-1]);
    sprev.copy(g.vertices[g.vertices.length-2]);
  }

  var v = new THREE.Vector3();
  v.subVectors(sprev, s);

  if (t.dot(v) < 0)
    t.multiplyScalar(-1);

  return t;
}


export const clone_body = withSpinner('Cloning body', async function clone_body(dont_attach_manipulator_afterwards = false) {
  if (uid_selected_faces.length == 0)
    return;

  suppress_animate();
  suppress_undo_tracking();

  var body_ids = [];
  uid_selected_faces.keys().forEach(function(fid){
    var bid = uil_get_body_of_face(fid);
    if (body_ids.indexOf(bid) == -1)
      body_ids.push(bid);
  });

  var selected_faces = [...uid_selected_faces.keys()];
  var faces_to_make_visible_for_each_body = [];

  // First make invisible any non-selected faces of the body
  body_ids.forEach(function(body_id){
    var faces_to_make_invisible = [];
    faces_of_body(body_id).forEach(function(fid){
      if (uil_is_object_visible(fid) && selected_faces.indexOf(fid) == -1) {
        push_nodups(fid, faces_to_make_invisible);
      }
    });
    uil_make_faces_invisible(faces_to_make_invisible, true);
    faces_to_make_visible_for_each_body.push(faces_to_make_invisible);
  });

  uid_deselect_all_objects();

  var copied_body_ids = uil_copy_bodies(body_ids, dont_attach_manipulator_afterwards);

  faces_to_make_visible_for_each_body.forEach(function(faces){
    uil_make_faces_visible(faces);
  });

  // Make sure we're not left with any visible/invisible tangent edge pairs
  remove_tangencies_from_visible_invisible_edge_pairs();

  // Don't introduce a separate undo step here, so that if the user copies and then moves the copied part and then
  // does an undo, they don't get to a coincident copy state. We need to introduce the undo step only if they
  // remove the manipulator without having moved the copied part, which we handle in uid_hide_rigid_move_manipulator().
  unsuppress_undo_tracking_if_ready(true);

  unsuppress_animate_if_ready();
});

export function make_edge_a_scaled_copy_of_other_edge(e1id, v1aid, v1bid, e2id, v2aid, v2bid) {
  // Receives two sets of (edge, vertex1, vertex2), where vertex1 and vertex2 are the two vertices on either end of the edge in the
  // intended order, and makes the second edge a scaled copy of the first edge, without moving any vertices.

  var A = new THREE.Vector3();
  A.copy(uid_vertices.getItem(v1aid).object3d.position);
  var B = new THREE.Vector3();
  B.copy(uid_vertices.getItem(v1bid).object3d.position);
  var C = new THREE.Vector3();
  C.copy(uil_get_point_at_edge_param(e1id, 0.5));
  var x = new THREE.Vector3();
  x.subVectors(B, A).normalize();

  var Ap = new THREE.Vector3();
  Ap.copy(uid_vertices.getItem(v2aid).object3d.position);
  var Bp = new THREE.Vector3();
  Bp.copy(uid_vertices.getItem(v2bid).object3d.position);
  var Cp = new THREE.Vector3();
  Cp.copy(uil_get_point_at_edge_param(e2id, 0.5));
  var xp = new THREE.Vector3();
  xp.subVectors(Bp, Ap).normalize();

  var eps = 0.0005;

  if (maths.proximate(v3.line_point_distance(C, A, x), 0, eps) ||
      maths.proximate(v3.line_point_distance(Cp, Ap, xp), 0, eps)) {
    display_message_to_user(LOG.WARN, "One of the edges is straight, unable to proceed with cloning.");
  }

  var N = new THREE.Vector3();
  var v1 = new THREE.Vector3();
  var v2 = new THREE.Vector3();
  v1.subVectors(C, A);
  v2.subVectors(B, A);
  N.crossVectors(v1, v2).normalize();
  var Np = new THREE.Vector3();
  v1.subVectors(Cp, Ap);
  v2.subVectors(Bp, Ap);
  Np.crossVectors(v1, v2).normalize();
  Np.normalize();

  // XXX OVERRIDE Np and use N instead, to give a copy with the same orientation
  Np.copy(N);

  var y = new THREE.Vector3();
  y.crossVectors(N, x).normalize();

  var yp = new THREE.Vector3();
  yp.crossVectors(Np, xp).normalize();

  var scale = v3.point_point_distance(A, B);
  var scalep = v3.point_point_distance(Ap, Bp);


  var e = uid_edges.getItem(e1id);
  var newpoints = [];
  e.object3d.geometry.vertices.forEach(function(p){
    // For any point p on the edge, the normalized coordinates on the local coordinate system are:
    var coords = get_local_system_coordinates(p, A, x, y, N, scale);
    // For local coordinates [x,y,z], the actual coordinates of the new point on the (copy of the) edge are:
    var pp = get_real_coordinates_from_local_coordinate_system(coords, Ap, xp, yp, Np, scalep);
    newpoints.push(pp);
  });

  // XXX We may need to invert newpoints, depending on order of selected vertices.
  if (v3.point_point_distance(Ap, newpoints[0]) > v3.point_point_distance(Ap, newpoints[newpoints.length-1])) {
    newpoints.reverse();
  }

  // Remove any tangencies from e2, to allow geometry modification.
  var es1 = uil_get_edges_of_vertex(v2aid);
  var es2 = uil_get_edges_of_vertex(v2bid);
  var es = es1.concat(es2);
  es.forEach(function(eid){
    if (eid != e2id) {
      uil_remove_joint_smoothness([[eid, e2id]], false, true);
    }
  });
  uil_modify_edge_geometry(e2id, v2aid, v2bid, newpoints);
}

export function get_local_system_coordinates(p, o, x, y, z, scale) {
  var px = v3.project_point_on_line(p, o, x);
  var dx = v3.point_point_distance(px, o) / scale;
  var v1 = new THREE.Vector3();
  v1.subVectors(px, o);
  if (v1.dot(x) < 0)
    dx *= -1;
  var py = v3.project_point_on_line(p, o, y);
  var dy = v3.point_point_distance(py, o) / scale;
  v1.subVectors(py, o);
  if (v1.dot(y) < 0)
    dy *= -1;
  var pz = v3.project_point_on_line(p, o, z);
  var dz = v3.point_point_distance(pz, o) / scale;
  v1.subVectors(pz, o);
  if (v1.dot(z) < 0)
    dz *= -1;

  return [dx, dy, dz];
}

export function get_real_coordinates_from_local_coordinate_system(coords, o, x, y, z, scale) {

  var x_coord = new THREE.Vector3(0,0,0);
  x_coord.addScaledVector(x, coords[0] * scale);
  var y_coord = new THREE.Vector3(0,0,0);
  y_coord.addScaledVector(y, coords[1] * scale);
  var z_coord = new THREE.Vector3(0,0,0);
  z_coord.addScaledVector(z, coords[2] * scale);

  var p = new THREE.Vector3(o.x, o.y, o.z);
  p.add(x_coord);
  p.add(y_coord);
  p.add(z_coord);
  return p;
}

export function check_if_faces_are_0_thickness(f1,f2) {

  // We'll collect a bunch of samples on each surface
  var b1 = new THREE.Vector3();
  var d1 = new THREE.Vector3();
  var n1 = new THREE.Vector3();
  var wv_r1 = new WrappedValue(0);
  var wv_f1 = new WrappedValue(0);
  uil_get_face_geometry(f1, b1, n1, wv_r1, d1, wv_f1);

  var b2 = new THREE.Vector3();
  var d2 = new THREE.Vector3();
  var n2 = new THREE.Vector3();
  var wv_r2 = new WrappedValue(0);
  var wv_f2 = new WrappedValue(0);
  uil_get_face_geometry(f2, b2, n2, wv_r2, d2, wv_f2);

  // If centers coincide within eps, call them matching
  if (b1.distanceTo(b2) < Math.min(wv_r1.v, wv_r2.v) / 100.0)
    return true;

  // If centers are less than the radius of the smaller face apart AND normals coincide within 1 degree,
  // check if they are indeed 0-thickness
  if (b1.distanceTo(b2) < Math.min(wv_r1.v, wv_r2.v) &&
      Math.abs(n1.angleTo(n2) - Math.PI) < Math.PI / 180.0) {

    // Do a check with the raycaster to see if we're hitting both faces
    var o = new THREE.Vector3();
    var hitf1 = false;
    var hitf2 = false;

    o.addVectors(b1, n1);
    uid_raycaster.set(o, n1.multiplyScalar(-1));
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    intersects.forEach(function(o) {
      var obj = uid_object3dIDToUid_object.getItem(o.object.id);
      if (obj && obj.id == f1)
        hitf1 = true;
        if (obj && obj.id == f2)
        hitf2 = true;
    });

    if (hitf1 && hitf2)
      return true;

    var hitf1 = false;
    var hitf2 = false;

    o.addVectors(b2, n2);
    uid_raycaster.set(o, n2.multiplyScalar(-1));
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    intersects.forEach(function(o) {
      var obj = uid_object3dIDToUid_object.getItem(o.object.id);
      if (obj && obj.id == f1)
      hitf1 = true;
      if (obj && obj.id == f2)
      hitf2 = true;
    });

    if (hitf1 && hitf2)
    return true;
  }

  // Otherwise return false (for now)
  return false;
}

export function from_3d_coordinates_to_2d_screen_coordinates(v) {
  // Takes a 3D vector and projects it onto the camera 2D plane.
  // Based on https://discourse.threejs.org/t/how-to-converting-world-coordinates-to-2d-mouse-coordinates-in-threejs/2251/3

  var pv = new THREE.Vector3();
  pv.copy(v);
  uid_camera.updateMatrixWorld();
  pv.project( uid_camera );
  var coords = new THREE.Vector2();
  coords.x = pv.x;
  coords.y = pv.y;
  return coords;
}

export function from_2d_canvas_coordinates_to_3d_world_coordinates(mouselocation = null) {
  // Takes a 2D vector where the user clicked on the camera plane, and returns the corresponding 3D world coordinates.

  if (!mouselocation) {
    mouselocation = new THREE.Vector2();
    mouselocation.copy(pointer_normalised);
  }

  var look_vector = get_camera_look_vector(uid_camera).normalize();
  var pos = new THREE.Vector3();
  pos.copy(uid_camera.position);
  var camera_width = uid_camera.left * 2.0;
  var camera_height = uid_camera.top * 2.0;

  var camera_plane_horizontal = new THREE.Vector3();
  camera_plane_horizontal.crossVectors(uid_camera.up, look_vector);
  var mouse3dlocation = new THREE.Vector3();
  mouse3dlocation.copy(uid_camera.position);
  mouse3dlocation.addScaledVector(camera_plane_horizontal, mouselocation.x * camera_width / 2.0);
  mouse3dlocation.addScaledVector(uid_camera.up, mouselocation.y * camera_height / 2.0);
  return mouse3dlocation;
}

export function roughSizeOfObject( object ) {
  // XXX
  var objectList = [];
  var stack = [ object ];
  var bytes = 0;

  while ( stack.length ) {
      var value = stack.pop();

      if ( typeof value === 'boolean' ) {
          bytes += 4;
      }
      else if ( typeof value === 'string' ) {
          bytes += value.length * 2;
      }
      else if ( typeof value === 'number' ) {
          bytes += 8;
      }
      else if
      (
          typeof value === 'object'
          && objectList.indexOf( value ) === -1
      )
      {
          objectList.push( value );

          for( var i in value ) {
              stack.push( value[ i ] );
          }
      }
  }
  return bytes;
}

export function decimalToHex(d, padding) {
  var hex = Number(d).toString(16);
  padding = typeof (padding) === "undefined" || padding === null ? padding = 2 : padding;

  while (hex.length < padding) {
      hex = "0" + hex;
  }

  return hex;
}

export function numberIncrement(el, transform) {
  const input = document.querySelector(el);
  if (!input) {
      return
  }
  input.value = parseInt(input.value) + transform
}

export function vertex_distance(v1id, v2id) {
  return v3.point_point_distance(uid_vertices.getItem(v1id).object3d.position, uid_vertices.getItem(v2id).object3d.position);
}